import React, { Fragment, useState } from "react";
import Slider from "react-slick";
import { useHistory, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Col, Row, Button, Form, FormGroup, Label, Input, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";

// ─── Partner config (injected by nginx before React loads) ───────────────────
const _cfg = () =>
  (typeof window !== "undefined" && window.APP_CONFIG) || {};
const appName  = _cfg().appName     || "Fincrest Systems";
const primary  = _cfg().primaryColor || "#545cd8";
const tagline  = _cfg().tagline     || "Automated Options Trading, Intelligently Managed";

// ─── Slide 1: Candlestick chart ──────────────────────────────────────────────
const CandlestickSlide = () => {
  const candles = [
    { x: 30,  top: 168, bot: 178, wt: 172, wb: 183, bull: false },
    { x: 57,  top: 154, bot: 168, wt: 150, wb: 172, bull: true  },
    { x: 84,  top: 148, bot: 158, wt: 145, wb: 163, bull: false },
    { x: 111, top: 135, bot: 148, wt: 131, wb: 152, bull: true  },
    { x: 138, top: 130, bot: 140, wt: 126, wb: 144, bull: false },
    { x: 165, top: 116, bot: 130, wt: 112, wb: 134, bull: true  },
    { x: 192, top: 110, bot: 120, wt: 107, wb: 124, bull: false },
    { x: 219, top: 96,  bot: 110, wt: 92,  wb: 114, bull: true  },
    { x: 246, top: 90,  bot: 100, wt: 87,  wb: 104, bull: false },
    { x: 273, top: 76,  bot: 90,  wt: 72,  wb: 94,  bull: true  },
    { x: 300, top: 62,  bot: 76,  wt: 58,  wb: 80,  bull: true  },
  ];
  const volumes = [5, 8, 4, 9, 6, 11, 7, 10, 5, 9, 13];

  return (
    <div style={{ width: "100%", height: "100vh", background: "linear-gradient(160deg, #050d1a 0%, #0d1f3c 100%)", display: "flex", flexDirection: "column", justifyContent: "space-between", overflow: "hidden" }}>
      {/* Chart area */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px 16px 0" }}>
        <svg viewBox="0 0 340 205" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", maxHeight: 220 }}>
          {/* Grid lines */}
          {[50, 100, 150].map(y => (
            <line key={y} x1="20" y1={y} x2="320" y2={y} stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
          ))}
          {/* X axis */}
          <line x1="20" y1="185" x2="320" y2="185" stroke="rgba(255,255,255,0.12)" strokeWidth="1" />

          {/* Candlesticks */}
          {candles.map((c, i) => (
            <g key={i}>
              {/* Wick */}
              <line x1={c.x} y1={c.wt} x2={c.x} y2={c.wb} stroke={c.bull ? "#22c55e" : "#ef4444"} strokeWidth="1.5" />
              {/* Body */}
              <rect x={c.x - 8} y={c.top} width="16" height={c.bot - c.top} fill={c.bull ? "#22c55e" : "#ef4444"} rx="1" fillOpacity={c.bull ? 0.95 : 0.8} />
              {/* Volume bar */}
              <rect x={c.x - 7} y={185 + (13 - volumes[i])} width="14" height={volumes[i]} fill={c.bull ? "#22c55e" : "#ef4444"} fillOpacity="0.35" rx="1" />
            </g>
          ))}

          {/* Moving average line */}
          <path d="M 30 173 C 80 163 130 143 180 120 S 260 85 300 68" fill="none" stroke="#fbbf24" strokeWidth="2" strokeOpacity="0.85" strokeLinecap="round" />

          {/* Trend arrow at right edge */}
          <polygon points="312,52 322,62 302,62" fill="#22c55e" fillOpacity="0.9" />

          {/* Price label */}
          <text x="326" y="67" fontSize="8" fill="#22c55e" fontFamily="monospace" fontWeight="bold">↑</text>
        </svg>
      </div>

      {/* Slide text */}
      <div className="slider-content" style={{ padding: "0 32px 56px", textAlign: "left" }}>
        <span style={{ display: "inline-block", background: "rgba(250,191,36,0.18)", color: "#fbbf24", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", padding: "3px 10px", borderRadius: 4, marginBottom: 10 }}>
          Systematic Trading
        </span>
        <h3 style={{ fontSize: "1.45rem", fontWeight: 700, lineHeight: 1.25, marginBottom: 10, color: "#fff" }}>
          Trade with Rules,<br />Not Emotions
        </h3>
        <p style={{ fontSize: "0.82rem", opacity: 0.65, lineHeight: 1.55, margin: 0, color: "#fff" }}>
          Rule-based execution removes gut feel from every decision.
          The engine watches. You approve. It executes.
        </p>
      </div>
    </div>
  );
};

// ─── Slide 2: Multi-account management ───────────────────────────────────────
const MultiAccountSlide = () => {
  const accounts = [
    { id: "ACC-001", pnl: "+$12,454", strats: "2 strategies", color: "#22c55e" },
    { id: "ACC-002", pnl: "+$8,230",  strats: "3 strategies", color: "#22c55e" },
    { id: "ACC-003", pnl: "+$5,670",  strats: "1 strategy",   color: "#22c55e" },
    { id: "ACC-004", pnl: "+$18,920", strats: "4 strategies", color: "#22c55e" },
  ];

  return (
    <div style={{ width: "100%", height: "100vh", background: "linear-gradient(160deg, #050e08 0%, #0a2212 100%)", display: "flex", flexDirection: "column", justifyContent: "space-between", overflow: "hidden" }}>
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px 20px 0" }}>
        <svg viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", maxHeight: 220 }}>
          {/* Account cards — 2×2 grid */}
          {accounts.map((a, i) => {
            const col = i % 2;
            const row = Math.floor(i / 2);
            const x = col * 158 + 4;
            const y = row * 96 + 4;
            return (
              <g key={i}>
                {/* Card background */}
                <rect x={x} y={y} width="140" height="82" rx="6" fill="rgba(255,255,255,0.055)" stroke="rgba(255,255,255,0.10)" strokeWidth="1" />
                {/* Status dot */}
                <circle cx={x + 12} cy={y + 14} r="4" fill="#22c55e" />
                <text x={x + 22} y={y + 18} fontSize="8" fill="rgba(255,255,255,0.5)" fontFamily="system-ui,sans-serif">{a.id}</text>
                {/* P&L */}
                <text x={x + 12} y={y + 44} fontSize="16" fontWeight="bold" fill={a.color} fontFamily="system-ui,sans-serif">{a.pnl}</text>
                {/* Strategies */}
                <text x={x + 12} y={y + 60} fontSize="8" fill="rgba(255,255,255,0.4)" fontFamily="system-ui,sans-serif">{a.strats}</text>
                {/* Mini sparkline */}
                <polyline points={`${x+12},${y+75} ${x+35},${y+70} ${x+58},${y+67} ${x+80},${y+62} ${x+102},${y+58} ${x+128},${y+52}`} fill="none" stroke="#22c55e" strokeWidth="1.5" strokeOpacity="0.5" strokeLinecap="round" strokeLinejoin="round" />
              </g>
            );
          })}

          {/* Bottom status bar */}
          <rect x="4" y="198" width="292" height="0" rx="0" fill="none" />
        </svg>
      </div>

      {/* Status bar */}
      <div style={{ margin: "0 20px 12px", background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.2)", borderRadius: 6, padding: "6px 12px", display: "flex", alignItems: "center", gap: 16 }}>
        <span style={{ fontSize: "0.7rem", color: "#22c55e", fontFamily: "monospace", fontWeight: 600 }}>● LIVE</span>
        <span style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.5)", fontFamily: "monospace" }}>10 accounts · 14s cycle · 2 pending</span>
      </div>

      {/* Slide text */}
      <div className="slider-content" style={{ padding: "0 32px 56px", textAlign: "left" }}>
        <span style={{ display: "inline-block", background: "rgba(34,197,94,0.15)", color: "#22c55e", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", padding: "3px 10px", borderRadius: 4, marginBottom: 10 }}>
          Parallel Execution
        </span>
        <h3 style={{ fontSize: "1.45rem", fontWeight: 700, lineHeight: 1.25, marginBottom: 10, color: "#fff" }}>
          Every Account.<br />Every Cycle.
        </h3>
        <p style={{ fontSize: "0.82rem", opacity: 0.65, lineHeight: 1.55, margin: 0, color: "#fff" }}>
          All accounts run concurrently every 5 minutes.
          One dashboard. Zero missed signals.
        </p>
      </div>
    </div>
  );
};

// ─── Slide 3: Options payoff diagram ─────────────────────────────────────────
const PayoffSlide = () => (
  <div style={{ width: "100%", height: "100vh", background: "linear-gradient(160deg, #0e050f 0%, #1a0a38 100%)", display: "flex", flexDirection: "column", justifyContent: "space-between", overflow: "hidden" }}>
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px 20px 0" }}>
      <svg viewBox="0 0 320 200" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", maxHeight: 220 }}>
        {/* Grid lines */}
        {[60, 110, 160].map(y => (
          <line key={y} x1="40" y1={y} x2="300" y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
        ))}

        {/* Axes */}
        <line x1="40" y1="165" x2="300" y2="165" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />
        <line x1="40" y1="20"  x2="40"  y2="165" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" />

        {/* Profit zone fill */}
        <path d="M 100 50 C 110 50 110 165 110 165 L 210 165 C 210 165 210 50 220 50 Z" fill="#a855f7" fillOpacity="0.08" />
        <path d="M 100 50 L 220 50 L 220 165 L 100 165 Z" fill="#a855f7" fillOpacity="0.06" />

        {/* Payoff curve */}
        <path d="M 42 155 L 75 155 C 88 155 92 50 100 50 L 220 50 C 228 50 232 155 245 155 L 298 155"
          fill="none" stroke="#a855f7" strokeWidth="2.5" strokeLinecap="round" />

        {/* Breakeven lines */}
        <line x1="100" y1="20" x2="100" y2="165" stroke="#ef4444" strokeWidth="1" strokeDasharray="4 3" strokeOpacity="0.7" />
        <line x1="220" y1="20" x2="220" y2="165" stroke="#ef4444" strokeWidth="1" strokeDasharray="4 3" strokeOpacity="0.7" />

        {/* Current price (center) */}
        <line x1="160" y1="20" x2="160" y2="165" stroke="rgba(255,255,255,0.2)" strokeWidth="1" strokeDasharray="3 4" />

        {/* Labels */}
        <text x="160" y="42" textAnchor="middle" fontSize="8" fill="#a855f7" fontFamily="system-ui,sans-serif" fontWeight="600">Max Premium Collected</text>
        <text x="100" y="178" textAnchor="middle" fontSize="7" fill="rgba(255,255,255,0.4)" fontFamily="system-ui,sans-serif">Put Strike</text>
        <text x="220" y="178" textAnchor="middle" fontSize="7" fill="rgba(255,255,255,0.4)" fontFamily="system-ui,sans-serif">Call Strike</text>
        <text x="170" y="178" textAnchor="middle" fontSize="7" fill="rgba(255,255,255,0.25)" fontFamily="system-ui,sans-serif">Underlying Price →</text>
        <text x="34" y="50" textAnchor="end" fontSize="7" fill="rgba(255,255,255,0.3)" fontFamily="system-ui,sans-serif">P&L</text>

        {/* Profit zone label */}
        <text x="160" y="115" textAnchor="middle" fontSize="8" fill="#a855f7" fontFamily="system-ui,sans-serif" fillOpacity="0.7">Profit Zone</text>
        <text x="160" y="128" textAnchor="middle" fontSize="7.5" fill="#a855f7" fontFamily="system-ui,sans-serif" fillOpacity="0.5">ΔHEDGE AUTO-MANAGED</text>

        {/* Loss zone indicators */}
        <text x="60" y="142" textAnchor="middle" fontSize="7" fill="#ef4444" fontFamily="system-ui,sans-serif" fillOpacity="0.6">Loss</text>
        <text x="265" y="142" textAnchor="middle" fontSize="7" fill="#ef4444" fontFamily="system-ui,sans-serif" fillOpacity="0.6">Loss</text>
      </svg>
    </div>

    {/* Slide text */}
    <div className="slider-content" style={{ padding: "0 32px 56px", textAlign: "left" }}>
      <span style={{ display: "inline-block", background: "rgba(168,85,247,0.15)", color: "#a855f7", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", padding: "3px 10px", borderRadius: 4, marginBottom: 10 }}>
        Adaptive Hedging
      </span>
      <h3 style={{ fontSize: "1.45rem", fontWeight: 700, lineHeight: 1.25, marginBottom: 10, color: "#fff" }}>
        Hedge Automatically.<br />Earn Monthly.
      </h3>
      <p style={{ fontSize: "0.82rem", opacity: 0.65, lineHeight: 1.55, margin: 0, color: "#fff" }}>
        Pyramid Hedging adapts your protection as you accumulate.
        Call premiums reduce your cost basis every expiry.
      </p>
    </div>
  </div>
);

// ─── Main Login component ─────────────────────────────────────────────────────
const Login = () => {
  const [formData, setFormData]           = useState({ username: "", password: "" });
  const [error, setError]                 = useState("");
  const [forgotOpen, setForgotOpen]       = useState(false);
  const [forgotUsername, setForgotUsername] = useState("");
  const [forgotStatus, setForgotStatus]   = useState(null); // null | "sending" | "done" | "error"
  const { login }                         = useAuth();
  const history                           = useHistory();
  const location                          = useLocation();

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const openForgot = () => {
    setForgotUsername("");
    setForgotStatus(null);
    setForgotOpen(true);
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotStatus("sending");
    try {
      await fetch("/api/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: forgotUsername }),
      });
      setForgotStatus("done");
    } catch {
      setForgotStatus("error");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await login(formData);
      const from = location.state?.from?.pathname;
      history.push(from && from !== "/pages/login" ? from : "/dashboard");
    } catch (err) {
      setError(err.response?.data?.message || "Login failed");
    }
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 700,
    arrows: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    fade: true,
    autoplay: true,
    autoplaySpeed: 5000,
    adaptiveHeight: false,
  };

  return (
    <Fragment>
      <div className="h-100">
        <Row className="h-100 g-0">
          {/* ── Left panel: slides ── */}
          {/* Scoped CSS: force height through every slick internal layer.
              fade=true makes slides position:absolute, removing them from
              normal flow, so .slick-list/.slick-track collapse to 0 height
              without this explicit cascade. */}
          <style>{`
            .login-slider .slick-slider,
            .login-slider .slick-list,
            .login-slider .slick-track { height: 100% !important; }
            .login-slider .slick-slide { height: 100% !important; }
            .login-slider .slick-slide > div { height: 100% !important; }
          `}</style>
          <Col lg="4" className="login-slider d-none d-lg-block"
               style={{ height: "100vh", overflow: "hidden", position: "relative", flexShrink: 0 }}>
            <div className="slider-light" style={{ height: "100%", overflow: "hidden" }}>
              <Slider {...sliderSettings}>
                <div><CandlestickSlide /></div>
                <div><MultiAccountSlide /></div>
                <div><PayoffSlide /></div>
              </Slider>
            </div>
          </Col>

          {/* ── Right panel: login form ── */}
          <Col lg="8" md="12" className="h-100 d-flex bg-white justify-content-center align-items-center">
            <Col lg="9" md="10" sm="12" className="mx-auto app-login-box">

              {/* ── Partner logo ── */}
              <div style={{ marginBottom: 28 }}>
                <img
                  src={_cfg().logoUrl || "/brand-logo.png"}
                  alt={appName}
                  style={{ maxHeight: 56, maxWidth: 220, objectFit: "contain", display: "block" }}
                  onError={(e) => {
                    // Logo missing — fall back to styled text
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "block";
                  }}
                />
                {/* Fallback text shown only if logo fails to load */}
                <div style={{ display: "none", fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif", fontWeight: 800, fontSize: "2rem", color: primary, letterSpacing: "-0.5px" }}>
                  {appName}
                </div>
              </div>

              <h4 className="mb-0">
                <div>Welcome back,</div>
                <span>Please sign in to your account.</span>
              </h4>

              <Row className="divider" />
              {error && <p style={{ color: "red" }}>{error}</p>}

              <div>
                <Form onSubmit={handleLogin}>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="username">Username</Label>
                        <Input
                          type="text"
                          name="username"
                          id="username"
                          placeholder="Username here..."
                          value={formData.username}
                          onChange={handleChange}
                          required
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label for="password">Password</Label>
                        <Input
                          type="password"
                          name="password"
                          id="password"
                          placeholder="Password here..."
                          value={formData.password}
                          onChange={handleChange}
                          required
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="divider" />
                  <div className="d-flex align-items-center">
                    <div className="ms-auto">
                      <a href="#" onClick={(e) => { e.preventDefault(); openForgot(); }} className="btn-lg btn btn-link">
                        Recover Password
                      </a>{" "}
                      <Button type="submit" color="primary" size="lg">
                        Login to Dashboard
                      </Button>
                    </div>
                  </div>
                </Form>
              </div>

            </Col>
          </Col>
        </Row>
      </div>
      {/* ── Recover Password Modal ── */}
      <Modal isOpen={forgotOpen} toggle={() => setForgotOpen(false)} centered>
        <ModalHeader toggle={() => setForgotOpen(false)}>Recover Password</ModalHeader>
        <ModalBody>
          {forgotStatus === "done" ? (
            <p className="mb-0" style={{ color: "#22c55e" }}>
              Your request has been sent to the administrator. They will reset your password shortly.
            </p>
          ) : forgotStatus === "error" ? (
            <p className="mb-0" style={{ color: "red" }}>
              Something went wrong. Please try again or contact your administrator directly.
            </p>
          ) : (
            <Form id="forgot-form" onSubmit={handleForgotSubmit}>
              <FormGroup>
                <Label for="forgot-username">Enter your username</Label>
                <Input
                  id="forgot-username"
                  type="text"
                  value={forgotUsername}
                  onChange={(e) => setForgotUsername(e.target.value)}
                  placeholder="Your username..."
                  required
                  autoFocus
                />
              </FormGroup>
              <p className="text-muted mb-0" style={{ fontSize: "0.82rem" }}>
                The administrator will be notified to reset your password.
              </p>
            </Form>
          )}
        </ModalBody>
        <ModalFooter>
          {forgotStatus === "done" || forgotStatus === "error" ? (
            <Button color="primary" onClick={() => setForgotOpen(false)}>Close</Button>
          ) : (
            <>
              <Button color="primary" type="submit" form="forgot-form" disabled={forgotStatus === "sending"}>
                {forgotStatus === "sending" ? "Sending…" : "Send Request"}
              </Button>
              <Button color="secondary" onClick={() => setForgotOpen(false)}>Cancel</Button>
            </>
          )}
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default Login;
